// Imports — File-based import wizard (live API)
import { useEffect, useState, type CSSProperties } from "react";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { Domain, ImportJob } from "@/lib/types";

const STEPS = ["Upload", "Map columns", "Commit"] as const;
const TARGET_FIELDS = ["id", "name", "code", "currency", "country", "industry", "(skip)"];

export function Imports() {
  const [tab, setTab] = useState<"wizard" | "history">("wizard");
  const [step, setStep] = useState(0);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [domainId, setDomainId] = useState<string>("instruments");
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [job, setJob] = useState<ImportJob | null>(null);
  const [history, setHistory] = useState<ImportJob[]>([]);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const toast = useToast();

  async function loadHistory() {
    try { setHistory(await api.imports.list()); } catch { /* ignored */ }
  }
  useEffect(() => {
    api.domains.list().then(setDomains).catch(() => { /* ignored */ });
    loadHistory();
  }, []);

  const onSelectFile = async (f: File | null) => {
    setFile(f);
    if (!f) { setColumns([]); setMapping({}); return; }
    // Best-effort header parse for CSV files in the browser.
    if (f.name.toLowerCase().endsWith(".csv")) {
      const text = await f.text().catch(() => "");
      const firstLine = text.split(/\r?\n/).find(l => l.trim()) ?? "";
      const cols = firstLine.split(",").map(c => c.trim().replace(/^"|"$/g, "")).filter(Boolean);
      setColumns(cols);
      const m: Record<string, string> = {};
      for (const c of cols) {
        const low = c.toLowerCase();
        if (low === "id" || low === "code" || low === "ticker") m[c] = "id";
        else if (low.includes("name")) m[c] = "name";
        else if (low.includes("currency") || low.includes("ccy")) m[c] = "currency";
        else if (low.includes("country")) m[c] = "country";
        else if (low.includes("industry") || low.includes("sector")) m[c] = "industry";
        else m[c] = "(skip)";
      }
      setMapping(m);
    } else {
      setColumns([]);
      setMapping({});
    }
  };

  const onUpload = async () => {
    if (!file || !domainId) return;
    setUploading(true);
    try {
      const j = await api.imports.upload(domainId, file);
      setJob(j);
      toast(`Uploaded — job ${j.id}`, "ok");
      setStep(1);
      await loadHistory();
    } catch (e) {
      toast(e instanceof Error ? `Upload failed: ${e.message}` : "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const onCommit = async () => {
    if (!job) return;
    setCommitting(true);
    try {
      const j = await api.imports.confirmMapping(job.id, mapping);
      setJob(j);
      toast(`Import ${j.status} — ${j.rowsOk ?? 0} ok, ${j.rowsError ?? 0} errors`, "ok");
      await loadHistory();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Commit failed", "error");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <header style={{ ...panel, padding: 20 }}>
        <div style={eyebrow}>BATCH</div>
        <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400 }}>Imports</div>
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          <button style={{ ...pillBtn, ...(tab === "wizard" ? pillBtnActive : {}) }} onClick={() => setTab("wizard")}>New import</button>
          <button style={{ ...pillBtn, ...(tab === "history" ? pillBtnActive : {}) }} onClick={() => { setTab("history"); loadHistory(); }}>History ({history.length})</button>
        </div>
      </header>

      {tab === "wizard" && (
        <>
          <div style={{ ...panel, display: "flex", alignItems: "center", gap: 0, padding: 16 }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0 }}>
                <button onClick={() => setStep(i)} style={{
                  width: 28, height: 28, borderRadius: "var(--r-pill)", border: "none",
                  background: i <= step ? "var(--md-primary)" : "var(--md-sc-high)",
                  color: i <= step ? "var(--md-on-primary)" : "var(--md-on-surface-var)",
                  font: "var(--t-label-md)", cursor: "pointer",
                }}>{i + 1}</button>
                <span style={{ marginLeft: 10, font: i === step ? "var(--t-title-sm)" : "var(--t-body-sm)" }}>{s}</span>
                {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, margin: "0 16px", background: i < step ? "var(--md-primary)" : "var(--md-outline-var)" }} />}
              </div>
            ))}
          </div>

          <div style={{ ...panel, padding: 24, minHeight: 320 }}>
            {step === 0 && (
              <div>
                <label style={{ display: "grid", gap: 4, marginBottom: 16 }}>
                  <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Target domain</span>
                  <select style={input} value={domainId} onChange={e => setDomainId(e.target.value)}>
                    {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </label>
                <label style={{
                  display: "grid", placeItems: "center", padding: 48,
                  border: "2px dashed var(--md-outline-var)", borderRadius: "var(--r-lg)",
                  background: "var(--md-sc-low)", cursor: "pointer",
                }}>
                  <input type="file" accept=".csv,.json,.xlsx" hidden onChange={e => onSelectFile(e.target.files?.[0] ?? null)} />
                  <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400 }}>
                    {file ? file.name : "Drop CSV / JSON / XLSX here"}
                  </div>
                  <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)", marginTop: 6 }}>
                    or click to browse · max 50MB
                  </div>
                </label>
              </div>
            )}
            {step === 1 && (
              <div>
                <div style={{ font: "var(--t-title-md)", marginBottom: 12 }}>Map source columns to target fields</div>
                {columns.length === 0 ? (
                  <div style={{ color: "var(--md-on-surface-var)" }}>
                    {file ? "Columns unavailable — non-CSV files use server-side parsing." : "Upload a file first."}
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 24px 1fr", gap: 8, alignItems: "center" }}>
                    <div style={eyebrow}>SOURCE</div><span /><div style={eyebrow}>TARGET</div>
                    {columns.map(c => (
                      <span key={c} style={{ display: "contents" }}>
                        <span style={{ font: "var(--t-mono-sm)", padding: "8px 10px", background: "var(--md-sc-low)", borderRadius: "var(--r-md)" }}>{c}</span>
                        <span style={{ textAlign: "center", color: "var(--md-tertiary)" }}>→</span>
                        <select value={mapping[c] ?? "(skip)"} onChange={e => setMapping({ ...mapping, [c]: e.target.value })} style={input}>
                          {TARGET_FIELDS.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {step === 2 && (
              <div>
                <div style={{ font: "var(--t-title-md)", marginBottom: 16 }}>Confirm and commit</div>
                <div style={{ marginBottom: 12 }}>Job <code className="font-mono">{job?.id ?? "(none)"}</code> · domain <code className="font-mono">{domainId}</code></div>
                {job?.status === "complete" ? (
                  <div role="status" aria-live="polite" style={{ marginTop: 12, padding: 14, background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", borderRadius: "var(--r-md)" }}>
                    ✓ Import complete — {job.rowsOk} ok, {job.rowsError} errors
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} style={pillBtn}>Back</button>
            {step === 0 && (
              <button onClick={onUpload} disabled={!file || uploading} style={filledBtn}>{uploading ? "Uploading…" : "Upload"}</button>
            )}
            {step === 1 && (
              <button onClick={() => setStep(2)} style={filledBtn}>Continue</button>
            )}
            {step === 2 && (
              <button onClick={onCommit} disabled={!job || committing} style={filledBtn}>{committing ? "Committing…" : "Commit import"}</button>
            )}
          </div>
        </>
      )}

      {tab === "history" && (
        <div style={{ ...panel, padding: 0 }}>
          {history.length === 0 ? (
            <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>No import jobs yet.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>Job</th><th style={th}>File</th><th style={th}>Status</th><th style={th}>Rows</th><th style={th}>Errors</th><th style={th}>Domain</th></tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                    <td style={{ ...td, font: "var(--t-mono-sm)" }}>{h.id}</td>
                    <td style={td}>{h.filename}</td>
                    <td style={td}>
                      <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                        background: h.status === "complete" ? "var(--md-secondary-container)" : h.status === "failed" ? "var(--md-error-container)" : "var(--md-tertiary-container)",
                        color: h.status === "complete" ? "var(--md-on-secondary-container)" : h.status === "failed" ? "var(--md-on-error-container)" : "var(--md-on-tertiary-container)" }}>
                        {h.status}
                      </span>
                    </td>
                    <td style={{ ...td, font: "var(--t-mono-sm)" }} className="tabular">{(h.rowsTotal ?? 0).toLocaleString()}</td>
                    <td style={{ ...td, color: (h.rowsError ?? 0) > 0 ? "var(--md-error)" : "var(--md-on-surface-var)", font: "var(--t-mono-sm)" }}>{h.rowsError ?? 0}</td>
                    <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{h.domainId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)", padding: 16 };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const input: CSSProperties = { padding: "8px 12px", borderRadius: "var(--r-md)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)", width: "100%" };
const pillBtn: CSSProperties = { padding: "6px 14px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };
const pillBtnActive: CSSProperties = { background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", borderColor: "transparent" };
const filledBtn: CSSProperties = { padding: "10px 20px", borderRadius: "var(--r-pill)", border: "none", background: "var(--md-primary)", color: "var(--md-on-primary)", cursor: "pointer", font: "var(--t-label-lg)" };
const th: CSSProperties = { textAlign: "left", padding: "10px 14px", font: "var(--t-label-md)", color: "var(--md-on-surface-var)" };
const td: CSSProperties = { padding: "10px 14px", font: "var(--t-body-md)" };

export default Imports;
